import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import {
  FileText, Send, Eye, Lock, Gavel, BarChart3, Archive, Users, Paperclip, CheckCircle, Activity, FileQuestion, Award, MessageSquare, TrendingDown, Shield,
} from 'lucide-react';
import AuctionControlRoomComponent from '../../components/AuctionControlRoom';
import { TYPE_LABELS, TYPE_COLORS } from './constants';
import { QuestionsTab } from './QuestionsTab';
import { EvaluationStagesTab } from './EvaluationStagesTab';
import { SuppliersTab } from './SuppliersTab';
import { DocumentsTab } from './DocumentsTab';
import { BidComparisonTab } from './BidComparisonTab';
import { AuctionItemsTab } from './AuctionItemsTab';
import { AuctionLotsTab } from './AuctionLotsTab';
import { AuctionEvaluationTab } from './AuctionEvaluationTab';
import { DetailsTab } from './DetailsTab';
import { AwardTab, CommunicationsTab } from './AwardTab';
import { SavingsTab } from './SavingsTab';
import { AuditTrailTab } from './AuditTrailTab';

export function RFxEditDialog({ open, onClose, event: initialEvent, fmtTz, defaultType }) {
  const isNew = !initialEvent;
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimer = useRef(null);

  const [form, setForm] = useState({
    title: '', description: '', type: defaultType || 'RFP',
    start_date: '', end_date: '', closing_date: '',
    sealed_bidding: { enabled: false, reveal_date: '' },
    auction_settings: { starting_price: '', minimum_decrement: '', auto_extend: true, extension_minutes: 5 },
    sections: [],
    invited_suppliers: [],
    documents: [],
    ...initialEvent,
  });

  useEffect(() => {
    if (initialEvent) {
      const norm = (d) => d ? d.substring(0, 16) : '';
      setForm(f => ({
        ...f,
        start_date: norm(initialEvent.start_date),
        end_date: norm(initialEvent.end_date),
        closing_date: norm(initialEvent.closing_date),
        sealed_bidding: {
          enabled: initialEvent.sealed_bidding?.enabled || false,
          reveal_date: norm(initialEvent.sealed_bidding?.reveal_date),
        },
      }));
    }
  }, [initialEvent]);

  const [eventRef, setEventRef] = useState(initialEvent?.event_ref || null);

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── Auto-save with debounce ────────────────────────────────────────────────
  const doSave = useCallback(async (formData, ref) => {
    if (!formData.title?.trim()) return ref;
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        closing_date: formData.closing_date || null,
        sealed_bidding: {
          enabled: formData.sealed_bidding?.enabled || false,
          reveal_date: formData.sealed_bidding?.reveal_date || null,
        },
      };
      // Remove fields that shouldn't be overwritten
      delete payload._id;
      delete payload.created_at;
      delete payload.created_by;

      if (!ref) {
        const r = await api.post('/api/rfx/events', payload);
        setEventRef(r.data.event_ref);
        setLastSaved(new Date());
        return r.data.event_ref;
      } else {
        // Use the autosave endpoint which saves ALL fields
        await api.post(`/api/rfx/events/${ref}/autosave`, payload);
        setLastSaved(new Date());
        return ref;
      }
    } catch (e) {
      console.warn('[RFx AutoSave] Failed:', e?.response?.data?.detail || e.message);
    }
    return ref;
  }, []);

  // Trigger auto-save when form changes (debounced 1s)
  useEffect(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      if (form.title?.trim()) doSave(form, eventRef);
    }, 1000);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [form, eventRef, doSave]);

  // Manual save
  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    const ref = await doSave(form, eventRef);
    if (ref) toast.success(`${ref} saved`);
    else toast.error('Save failed');
    setSaving(false);
  };

  // Publish with validation
  const handlePublish = async () => {
    // Client-side validation first
    const missing = [];
    if (!form.title?.trim()) missing.push('Title');
    if (!form.description?.trim()) missing.push('Description');
    if (!form.start_date) missing.push('Start Date');
    if (!form.end_date) missing.push('End Date');
    if (!form.closing_date) missing.push('Closing Date');
    if (missing.length > 0) {
      toast.error(`Cannot publish — missing: ${missing.join(', ')}`);
      setActiveTab('details');
      return;
    }
    // Save first, then publish
    const ref = eventRef || await doSave(form, eventRef);
    if (!ref) { toast.error('Save the event first'); return; }
    try {
      await api.post(`/api/rfx/events/${ref}/publish`);
      toast.success('Published!');
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Publish failed')); }
  };

  const isAuction = form.type === 'REVERSE_AUCTION';
  const isLive = isAuction && ['OPEN', 'PAUSED'].includes(form.status);
  const showAward = eventRef && !['DRAFT'].includes(form.status);
  const tabs = isAuction ? [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'questions', label: 'Questions', icon: FileQuestion },
    { id: 'items', label: 'Items', icon: Archive },
    { id: 'lots', label: 'Lots', icon: Archive },
    ...(isAuction && form.status !== 'DRAFT' ? [{ id: 'control', label: 'Control', icon: Activity }] : []),
    { id: 'suppliers', label: 'Suppliers', icon: Users },
    { id: 'documents', label: 'Docs', icon: Paperclip },
    ...(eventRef && form.status !== 'DRAFT' ? [{ id: 'bids', label: 'Evaluate', icon: BarChart3 }] : []),
    ...(showAward ? [{ id: 'award', label: 'Award', icon: Award }] : []),
    ...(showAward ? [{ id: 'savings', label: 'Savings', icon: TrendingDown }] : []),
    ...(eventRef ? [{ id: 'audit', label: 'Audit', icon: Shield }] : []),
  ] : [
    { id: 'details', label: 'Details', icon: FileText },
    { id: 'questions', label: 'Questions', icon: FileQuestion },
    { id: 'stages', label: 'Stages', icon: CheckCircle },
    { id: 'suppliers', label: 'Suppliers', icon: Users },
    { id: 'documents', label: 'Docs', icon: Paperclip },
    ...(eventRef && form.status !== 'DRAFT' ? [{ id: 'bids', label: 'Bids', icon: BarChart3 }] : []),
    ...(showAward ? [{ id: 'award', label: 'Award', icon: Award }] : []),
    ...(showAward ? [{ id: 'savings', label: 'Savings', icon: TrendingDown }] : []),
    ...(eventRef ? [{ id: 'audit', label: 'Audit', icon: Shield }] : []),
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[85vh] overflow-hidden flex flex-col" data-testid="rfx-edit-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {eventRef && <Badge className={`${TYPE_COLORS[form.type]} text-xs`}>{TYPE_LABELS[form.type]}</Badge>}
            {eventRef ? `${eventRef} — ${form.title || 'Untitled'}` : `New ${TYPE_LABELS[form.type] || 'Event'}`}
            {lastSaved && <span className="text-[10px] text-slate-400 font-normal ml-auto">Auto-saved {lastSaved.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Los_Angeles' })}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex border-b gap-0 flex-shrink-0 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-1" style={{ minHeight: '460px' }}>
          {activeTab === 'details' && <DetailsTab form={form} updateField={updateField} setForm={setForm} isNew={isNew} />}
          {activeTab === 'questions' && <QuestionsTab sections={form.sections} setSections={s => updateField('sections', s)} eventRef={eventRef} form={form} />}
          {activeTab === 'stages' && !isAuction && <EvaluationStagesTab stages={form.evaluation_stages || []} setStages={s => updateField('evaluation_stages', s)} />}
          {activeTab === 'items' && isAuction && <AuctionItemsTab items={form.auction_items || []} setItems={v => updateField('auction_items', v)} lots={form.auction_lots || []} eventRef={eventRef} />}
          {activeTab === 'lots' && isAuction && <AuctionLotsTab lots={form.auction_lots || []} setLots={v => updateField('auction_lots', v)} items={form.auction_items || []} setItems={v => updateField('auction_items', v)} />}
          {activeTab === 'control' && eventRef && <AuctionControlRoomComponent eventRef={eventRef} />}
          {activeTab === 'suppliers' && <SuppliersTab invited={form.invited_suppliers} setInvited={s => updateField('invited_suppliers', s)}
            interested={initialEvent?.interested_suppliers || []} eventRef={eventRef} form={form} />}
          {activeTab === 'documents' && <DocumentsTab documents={form.documents} eventRef={eventRef}
            setDocuments={d => updateField('documents', d)} />}
          {activeTab === 'bids' && eventRef && !isAuction && <BidComparisonTab eventRef={eventRef} sections={form.sections} />}
          {activeTab === 'bids' && eventRef && isAuction && <AuctionEvaluationTab eventRef={eventRef} />}
          {activeTab === 'award' && eventRef && <AwardTab eventRef={eventRef} form={form} onClose={onClose} />}
          {activeTab === 'savings' && eventRef && <SavingsTab eventRef={eventRef} form={form} />}
          {activeTab === 'audit' && eventRef && <AuditTrailTab eventRef={eventRef} />}
        </div>

        <div className="flex items-center justify-between border-t pt-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <div className="flex gap-2">
            {eventRef && form.status === 'DRAFT' && (
              <Button variant="outline" className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={handlePublish} data-testid="publish-btn">
                <Send className="w-4 h-4 mr-1" /> Publish
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-rfx-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
